import { IAllCruisesList, ICruise } from "@/interfaces/IAllCruisesList";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

const parsePrice = (priceString: string): number => {
  // Handle empty strings, "0", and invalid values
  if (!priceString || priceString === "0") return 0;
  const parsed = Math.round(parseFloat(priceString) * 100) / 100;
  return isNaN(parsed) ? 0 : parsed;
};

const PER_PAGE = 10;

export type CabinType = "flyCruise" | "cruiseOnly" | "inside" | "outside" | "balcony" | "suite"; //cruiseOnly default price

export interface IQuery {
  from: string | null;
}

// Helper function to normalize dates for comparison
const normalizeDates = (dateStr: string): string => {
  const date = new Date(dateStr);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

type InitialState = {
  query: IQuery | undefined | null;
  responce: IAllCruisesList | null;
  originalAllResults: ICruise[];
  filteredResults: ICruise[];
  filterResultui: ICruise[];
  currentPage: number;
  priceRange: [number, number];
  isLoading: boolean;
  selectedCabinType: CabinType;
  minPrices: Record<CabinType, number>;
  maxPrices: Record<CabinType, number>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dayRange: any[];
  minDay: number;
  maxDay: number;
  totalPages: number;
  filteredResultsCount: number;
  hasNextPage: boolean;
  operators: string[];
  selectedOperator: string[];
  availableRegions: string[];
  selectedRegions: string[];
  availableCruiseTypes: string[];
  selectedCruiseTypes: string[];
  selectedShipName: string[];
  shipName: string[];
  dateGroup: string[];
  selectedCruiseDate: string[];
  StartPoint: string[];
  selectedStartPoint: string[];
  EndPoint: string[];
  selectedEndPoint: string[];
  clearfilterResult: boolean;
  rating: string[];
  SelectedRating: string[];
  priceTypeCounts: {
    flyCruise: number;
    cruiseOnly: number;
    inside: number;
    outside: number;
    balcony: number;
    suite: number;
  };
};

const initialState: InitialState = {
  query: null,
  responce: null,
  originalAllResults: [],
  filteredResults: [],
  filterResultui: [],
  currentPage: 1,
  priceRange: [0, 0],
  isLoading: true,
  selectedCabinType: "cruiseOnly",
  minPrices: {
    flyCruise: 0,
    cruiseOnly: 0,
    inside: 0,
    outside: 0,
    balcony: 0,
    suite: 0,
  },
  maxPrices: {
    flyCruise: 0,
    cruiseOnly: 0,
    inside: 0,
    outside: 0,
    balcony: 0,
    suite: 0,
  },
  minDay: 0,
  maxDay: 0,
  dayRange: [0, 0],
  totalPages: 0,
  filteredResultsCount: 0,
  hasNextPage: false,
  selectedOperator: [],
  operators: [],
  availableRegions: [],
  availableCruiseTypes: [],
  selectedRegions: [],
  selectedCruiseTypes: [],
  selectedShipName: [],
  shipName: [],
  dateGroup: [],
  selectedCruiseDate: [],
  StartPoint: [],
  selectedStartPoint: [],
  EndPoint: [],
  selectedEndPoint: [],
  clearfilterResult: false,
  rating: [],
  SelectedRating: [],
  priceTypeCounts: {
    flyCruise: 0,
    cruiseOnly: 0,
    inside: 0,
    outside: 0,
    balcony: 0,
    suite: 0,
  },
};

const cruiseSlice = createSlice({
  name: "cruises",
  initialState,
  reducers: {
    setItems(
      state,
      action: PayloadAction<{
        query?: IQuery;
        responce: IAllCruisesList;
        isLoadMore?: boolean;
      }>,
    ) {
      // not use any where query add if need future implement modify search like
      state.query = action.payload.query;
      // start

      
      if (action.payload.isLoadMore) {
        state.originalAllResults = [
          ...state.originalAllResults,
          ...action.payload.responce.cruises,
        ];

        state.responce = {
          ...action.payload.responce,
          cruises: [...state.originalAllResults],
        };
      } else {
        state.responce = action.payload.responce;
        state.originalAllResults = [...action.payload.responce.cruises];
      }
      cruiseSlice.caseReducers.initializeFilters(state);
      cruiseSlice.caseReducers.postUpdate(state);
    },

    initializeFilters(state) {
      const prices = {
        cruiseOnly: [] as number[],
        inside: [] as number[],
        outside: [] as number[],
        balcony: [] as number[],
        suite: [] as number[],
        flyCruise: [] as number[],
      };

      // Get unique operators from cruise data
      const uniqueOperators = Array.from(
        new Set(state.originalAllResults.map((cruise) => cruise.operatorTitle)),
      ).sort();
      state.operators = uniqueOperators;

      // Get unique shipName
      const uniqueShipname = Array.from(
        new Set(state.originalAllResults.map((cruise) => cruise.shipTitle)),
      ).sort();
      state.shipName = uniqueShipname;

      // region
      const allRegions = state.originalAllResults.flatMap((cruise) => cruise.regions);
      state.availableRegions = Array.from(new Set(allRegions)).sort();

      //  cruise type oceon river
      const allCruiseTypes = state.originalAllResults.flatMap((cruise) => cruise.cruiseType);
      state.availableCruiseTypes = Array.from(new Set(allCruiseTypes)).sort();

      //locations
      const locationStarts = Array.from(
        new Set(state.originalAllResults.map((cruise) => cruise.startsAt)),
      ).sort();
      state.StartPoint = locationStarts;

      // RatingTypd
      const Rating = Array.from(
        new Set(state.originalAllResults.map((cruise) => cruise.rating)),
      ).sort();
      state.rating = Rating;

      if (state.selectedStartPoint.length > 0) {
        // Filter cruises based on selected start points
        const filteredCruises = state.originalAllResults.filter((cruise) =>
          state.selectedStartPoint.includes(cruise.startsAt),
        );

        // Get unique end points from filtered cruises
        const locationEnds = Array.from(
          new Set(filteredCruises.map((cruise) => cruise.endsAt)),
        ).sort();
        state.EndPoint = locationEnds;
      } else {
        // If no start points selected, show all possible end points
        const locationEnds = Array.from(
          new Set(state.originalAllResults.map((cruise) => cruise.endsAt)),
        ).sort();
        state.EndPoint = locationEnds;
      }

      // price by type
      state.originalAllResults.forEach((cruise) => {
        // if river flyCruisePrice
        const flyCruiseOnlyprice = parsePrice(cruise.flyCruisePrice);
        const cruiseOnlyPrice = parsePrice(cruise.cruiseOnlyPrice);
        const insidePrice = parsePrice(cruise.insidePrice);
        const outsidePrice = parsePrice(cruise.outsidePrice);
        const balconyPrice = parsePrice(cruise.balconyPrice);
        const suitePrice = parsePrice(cruise.suitePrice);

        prices.flyCruise.push(flyCruiseOnlyprice);
        prices.cruiseOnly.push(cruiseOnlyPrice);
        prices.inside.push(insidePrice);
        prices.outside.push(outsidePrice);
        prices.balcony.push(balconyPrice);
        prices.suite.push(suitePrice);
      });

      // Set min/max for each price type
      Object.keys(prices).forEach((key) => {
        const priceType = key as keyof typeof prices;
        // if we want remove 0 price result
        // const validPrices = prices[priceType].filter((price) => price > 0);
        const validPrices = prices[priceType];

        if (validPrices.length > 0) {
          state.minPrices[priceType] = Math.min(...validPrices);
          state.maxPrices[priceType] = Math.max(...validPrices);
        } else {
          // If no valid prices found, set both min and max to 0
          state.minPrices[priceType] = 0;
          state.maxPrices[priceType] = 0;
        }
      });

      // // Initialize price range based on selected price type if not set
      const selectedType = state.selectedCabinType;
      if (state.priceRange[0] === 0 && state.priceRange[1] === 0) {
        state.priceRange = [state.minPrices[selectedType], state.maxPrices[selectedType]];
      }

      // initialize day for any
      const nights = state.originalAllResults.map((item) => item.cruiseNights);
      state.minDay = Math.min(...nights);
      state.maxDay = Math.max(...nights);

      if (state.dayRange[0] === 0 && state.dayRange[1] === 0) {
        state.dayRange = [state.minDay, state.maxDay];
      }

      // if (nights.length > 0) {
      //   state.minDay = Math.min(...nights);
      //   state.maxDay = Math.max(...nights);

      //   // Always initialize dayRange here
      //   state.dayRange = [state.minDay, state.maxDay];
      // } else {
      //   // Handle empty case
      //   state.minDay = 0;
      //   state.maxDay = 0;
      //   state.dayRange = [0, 0];
      // }

      //date filter by all result
      const dateDealFilter = Array.from(
        new Set(state.originalAllResults.map((cruise) => cruise.startsOn)),
      ).sort();
      state.dateGroup = dateDealFilter;

      // ---------end--------
    },

    setPriceType(state, action: PayloadAction<CabinType>) {
      const newCabinType = action.payload;

      // Set price range based on the new cabin type
      const newMinPrice = state.minPrices[newCabinType];
      const newMaxPrice = state.maxPrices[newCabinType];

      // Always update price range to the new cabin type's range
      state.priceRange = [newMinPrice, newMaxPrice];

      state.selectedCabinType = newCabinType;

      // Reset to first page and update results
      state.currentPage = 1;
      cruiseSlice.caseReducers.postUpdate(state);
    },

    setDaysFilter(state, action: PayloadAction<[number, number]>) {
      state.dayRange = action.payload;
      state.currentPage = 1;
      cruiseSlice.caseReducers.postUpdate(state);
    },

    setPriceFilter(state, action: PayloadAction<[number, number]>) {
      state.priceRange = action.payload;
      state.currentPage = 1;
      cruiseSlice.caseReducers.postUpdate(state);
    },

    setOperator(state, action: PayloadAction<string[]>) {
      state.selectedOperator = action.payload;
      state.currentPage = 1;
      cruiseSlice.caseReducers.postUpdate(state);
    },
    setRegions(state, action: PayloadAction<string[]>) {
      state.selectedRegions = action.payload;
      state.currentPage = 1;
      cruiseSlice.caseReducers.postUpdate(state);
    },

    setCruiseTypes(state, action: PayloadAction<string[]>) {
      state.selectedCruiseTypes = action.payload;
      state.currentPage = 1;

      cruiseSlice.caseReducers.postUpdate(state);
    },

    setShipByName(state, action: PayloadAction<string[]>) {
      state.selectedShipName = action.payload;
      state.currentPage = 1;
      cruiseSlice.caseReducers.postUpdate(state);
    },

    setRating(state, action: PayloadAction<string[]>) {
      state.SelectedRating = action.payload;
      state.currentPage = 1;
      cruiseSlice.caseReducers.postUpdate(state);
    },

    setDayRangeFromButton(state, action) {
      const payload = action.payload;
      if (Array.isArray(payload)) {
        state.dayRange = payload;
      } else if (payload === "1-2") {
        state.dayRange = [state.minDay, 7];
      } else if (payload === "2-3") {
        state.dayRange = [7, 14];
      } else if (payload === "3-4") {
        state.dayRange = [14, 21];
      } else if (payload === "5") {
        state.dayRange = [21, state.maxDay];
      } else {
        state.dayRange = [state.minDay, state.maxDay];
      }
      state.currentPage = 1;
      // Trigger any post-update logic
      cruiseSlice.caseReducers.postUpdate(state);
    },

    setDateByCruise(state, action) {
      state.selectedCruiseDate = action.payload;
      state.currentPage = 1;
      cruiseSlice.caseReducers.postUpdate(state);
    },

    setStartLocation(state, action: PayloadAction<string[]>) {
      state.selectedStartPoint = action.payload;
      state.clearfilterResult = true;
      state.currentPage = 1;

      // Update available end points based on selected start points
      if (action.payload.length > 0) {
        // Get all possible end points for all selected start points
        const endPointsByStartPoint = action.payload.map((startPoint) => {
          return state.originalAllResults
            .filter((cruise) => cruise.startsAt === startPoint)
            .map((cruise) => cruise.endsAt);
        });

        // Flatten and deduplicate the end points
        const allEndPoints = Array.from(new Set(endPointsByStartPoint.flat())).sort();

        state.EndPoint = allEndPoints;
      } else {
        // If no start points are selected, show all possible end points
        const allEndPoints = Array.from(
          new Set(state.originalAllResults.map((cruise) => cruise.endsAt)),
        ).sort();
        state.EndPoint = allEndPoints;
      }

      cruiseSlice.caseReducers.postUpdate(state);
    },
    setEndLocation(state, action: PayloadAction<string[]>) {
      state.selectedEndPoint = action.payload;
      state.currentPage = 1;
      cruiseSlice.caseReducers.postUpdate(state);
      state.clearfilterResult = true;
    },

    setLocationclear(state) {
      state.selectedStartPoint = [];
      state.selectedEndPoint = [];
      state.clearfilterResult = false;

      // Reset end points to show all possibilities
      const locationEnds = Array.from(
        new Set(state.originalAllResults.map((cruise) => cruise.endsAt)),
      ).sort();
      state.EndPoint = locationEnds;
      cruiseSlice.caseReducers.postUpdate(state);
    },

    // -------------------------------filtr result end

    setPage(state, action: PayloadAction<number>) {
      state.currentPage = action.payload;
      cruiseSlice.caseReducers.postUpdate(state);
    },

    // ======================update count only =========================
    updatePriceTypeCounts(state) {
      // Reset all counts
      const cabinTypes: CabinType[] = [
        "flyCruise",
        "cruiseOnly",
        "inside",
        "outside",
        "balcony",
        "suite",
      ];
      cabinTypes.forEach((type) => {
        state.priceTypeCounts[type] = 0;
      });

      // For each cruise in the original results
      state.originalAllResults.forEach((cruise) => {
        // Get current filter conditions (excluding price and cabin type)
        const nights = cruise.cruiseNights;
        let nightsInRange = nights >= state.dayRange[0] && nights <= state.dayRange[1];

        if (state.dayRange[0] === 0 && state.dayRange[1] === 0 && state.minDay > 0) {
          state.dayRange = [state.minDay, state.maxDay];
          nightsInRange = nights >= state.minDay && nights <= state.maxDay;
        }

        const operatorMatch =
          !state.selectedOperator ||
          state.selectedOperator.length === 0 ||
          state.selectedOperator.includes(cruise.operatorTitle);

        const regionMatch =
          state.selectedRegions.length === 0 ||
          cruise.regions.some((region) => state.selectedRegions.includes(region));

        const cruiseTypeMatch =
          state.selectedCruiseTypes.length === 0 ||
          cruise.cruiseType.some((crtype) => state.selectedCruiseTypes.includes(crtype));

        const ShipMatch =
          !state.selectedShipName ||
          state.selectedShipName.length === 0 ||
          state.selectedShipName.includes(cruise.shipTitle);

        const RatingMatch =
          !state.SelectedRating ||
          state.SelectedRating.length === 0 ||
          state.SelectedRating.includes(cruise.rating);

        const checkDateMatch = (): boolean => {
          if (!state.selectedCruiseDate || state.selectedCruiseDate.length === 0) {
            return true;
          }
          const cruiseStartDate = normalizeDates(cruise.startsOn);
          const match = state.selectedCruiseDate.some(
            (selectedDate) => normalizeDates(selectedDate) === cruiseStartDate,
          );
          return match;
        };
        const Datematch = checkDateMatch();

        const locationStartMatch =
          !state.selectedStartPoint ||
          state.selectedStartPoint.length === 0 ||
          state.selectedStartPoint.includes(cruise.startsAt);

        const locationEndMatch =
          !state.selectedEndPoint ||
          state.selectedEndPoint.length === 0 ||
          state.selectedEndPoint.includes(cruise.endsAt);

        // Base match conditions excluding price
        const baseMatch =
          nightsInRange &&
          operatorMatch &&
          regionMatch &&
          cruiseTypeMatch &&
          ShipMatch &&
          Datematch &&
          locationStartMatch &&
          locationEndMatch &&
          RatingMatch;

        // If base conditions match, check each cabin type
        if (baseMatch) {
          cabinTypes.forEach((type) => {
            const price = (() => {
              switch (type) {
                case "cruiseOnly":
                  return parsePrice(cruise.cruiseOnlyPrice);
                case "inside":
                  return parsePrice(cruise.insidePrice);
                case "outside":
                  return parsePrice(cruise.outsidePrice);
                case "balcony":
                  return parsePrice(cruise.balconyPrice);
                case "suite":
                  return parsePrice(cruise.suitePrice);
                case "flyCruise":
                  return parsePrice(cruise.flyCruisePrice);
              }
            })();

            // Only count if the price is within the range for this specific cabin type
            // We're using the min/max prices for each type rather than current price range
            if (price > 0 && price >= state.minPrices[type] && price <= state.maxPrices[type]) {
              state.priceTypeCounts[type]++;
            }
          });
        }
      });
    },
    // ====================================

    // commone clecar filter
    clearFilters(state) {
      const selectedType = state.selectedCabinType;
      // Reset price range based on currently selected price type
      state.priceRange = [state.minPrices[selectedType], state.maxPrices[selectedType]];
      state.selectedOperator = [];
      state.selectedRegions = [];
      state.selectedCruiseTypes = [];
      state.selectedShipName = [];
      state.selectedStartPoint = [];
      state.selectedEndPoint = [];
      state.selectedCruiseDate = [];
      state.SelectedRating = [];
      state.dayRange = [state.minDay, state.maxDay];
      state.currentPage = 1;
      cruiseSlice.caseReducers.postUpdate(state);
    },

    // updaye latsly from this this shold fim=nam updaye all thisngss
    postUpdate(state) {
      state.isLoading = true;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const getPriceForType = (cruise: ICruise, type: CabinType): any => {
        const price = (() => {
          switch (type) {
            case "cruiseOnly":
              return parsePrice(cruise.cruiseOnlyPrice);
            case "inside":
              return parsePrice(cruise.insidePrice);
            case "outside":
              return parsePrice(cruise.outsidePrice);
            case "balcony":
              return parsePrice(cruise.balconyPrice);
            case "suite":
              return parsePrice(cruise.suitePrice);
            case "flyCruise":
              return parsePrice(cruise.flyCruisePrice);
          }
        })();
        return price;
      };

      // Apply both price and day filters to original results
      const selectedType = state.selectedCabinType;

      const filteredResult = state.originalAllResults.filter((cruise) => {
        const price = getPriceForType(cruise, selectedType);
        // if price 0 filter that to remove that card in here
        // if (price === 0 || price === undefined) {
        //   return false;
        // }
        const nights = cruise.cruiseNights;

        const priceInRange = price >= state.priceRange[0] && price <= state.priceRange[1];
        const nightsInRange = nights >= state.dayRange[0] && nights <= state.dayRange[1];
        const operatorMatch =
          !state.selectedOperator || // if no operator selected (empty array)
          state.selectedOperator.length === 0 || // also handles empty array case
          state.selectedOperator.includes(cruise.operatorTitle); // check if operator is in selected

        const regionMatch =
          state.selectedRegions.length === 0 ||
          cruise.regions.some((region) => state.selectedRegions.includes(region));

        // cruise stype

        // Simplified cruise type matching
        const cruiseTypeMatch =
          state.selectedCruiseTypes.length === 0 || // if
          cruise.cruiseType.some((crtype) => state.selectedCruiseTypes.includes(crtype));

        // shipTitle
        const ShipMatch =
          !state.selectedShipName || // if no ShipName selected (empty array)
          state.selectedShipName.length === 0 || // also handles empty array case
          state.selectedShipName.includes(cruise.shipTitle); // check if ShipName is in selected

        // Selecter rating type
        const RatingMatch =
          !state.SelectedRating ||
          state.SelectedRating.length === 0 ||
          state.SelectedRating.includes(cruise.rating);

        // date by filter
        const checkDateMatch = (cruise: ICruise, selectedDates: string[]): boolean => {
          if (!selectedDates || selectedDates.length === 0) {
            return true;
          }
          const cruiseStartDate = normalizeDates(cruise.startsOn);
          const match = selectedDates.some(
            (selectedDate) => normalizeDates(selectedDate) === cruiseStartDate,
          );
          return match;
        };
        const Datematch = checkDateMatch(cruise, state.selectedCruiseDate);

        // Locationstart
        const locationStartMatch =
          !state.selectedStartPoint || //
          state.selectedStartPoint.length === 0 ||
          state.selectedStartPoint.includes(cruise.startsAt);

        // Locationend
        const locationEndMatch =
          !state.selectedEndPoint ||
          state.selectedEndPoint.length === 0 ||
          state.selectedEndPoint.includes(cruise.endsAt);

        // Process dates and add isEarly property
        // state.originalAllResults = action.payload.responce.cruises.map((cruise) => ({
        //   ...cruise,
        //   formattedDate: formatDate(cruise.startsOn),
        //   yearMonth: getYearMonth(cruise.startsOn),
        //   isEarly: isEarlyCruise(cruise.startsOn),
        // }));

        // // Group cruises by month
        // const groups: IDateGroups = {};
        // state.originalAllResults.forEach((cruise) => {
        //   const yearMonth = cruise.startsOn;
        //   if (!groups[yearMonth]) {
        //     groups[yearMonth] = [];
        //   }
        //   groups[yearMonth].push(cruise);
        // });
        // state.dateGroups = groups;

        return (
          priceInRange &&
          nightsInRange &&
          operatorMatch &&
          regionMatch &&
          cruiseTypeMatch &&
          ShipMatch &&
          Datematch &&
          locationStartMatch &&
          locationEndMatch &&
          RatingMatch
        );
      });

      // Enhanced price sorting function
      const sortedResults = filteredResult.sort((a, b) => {
        const priceA = getPriceForType(a, selectedType);
        const priceB = getPriceForType(b, selectedType);

        // Handle cases where price might be undefined or zero
        if (priceA === undefined || priceA === 0) return 1; // Move undefined
        if (priceB === undefined || priceB === 0) return -1; // Move undefined

        return priceA - priceB; // Ascending order
      });

      state.filteredResults = sortedResults;

      // when every filter change state change count added
      cruiseSlice.caseReducers.updatePriceTypeCounts(state);

      // Calculate pagination values
      const startIndex = (state.currentPage - 1) * PER_PAGE;
      const endIndex = startIndex + PER_PAGE;

      state.filterResultui = state.filteredResults.slice(startIndex, endIndex);

      // Update pagination metadata
      state.filteredResultsCount = state.filteredResults.length;
      state.totalPages = Math.ceil(state.filteredResults.length / PER_PAGE);
      state.hasNextPage = state.currentPage < state.totalPages;
      // page pagination

      state.isLoading = false;
    },
  },
});

export const {
  setItems,
  setDaysFilter,
  setPriceFilter,
  setPage,
  clearFilters,
  setDayRangeFromButton,
  setPriceType,
  setOperator,
  setCruiseTypes,
  setRegions,
  setShipByName,
  setStartLocation,
  setEndLocation,
  setDateByCruise,
  setLocationclear,
  setRating,
} = cruiseSlice.actions;

export default cruiseSlice.reducer;
